/**
 * Customer Return Flow Configuration
 *
 * Centralized configuration for the customer return process including
 * session management, item defaults, shipping status thresholds, and
 * custom interface types.
 */

/**
 * @typedef {Object} CustomerReturnConfig
 * @property {Object} session - Session-related configuration
 * @property {number} session.TIMEOUT_MS - Session timeout in milliseconds
 * @property {Object} item - Item-related defaults
 * @property {number} item.DEFAULT_SELECTED_QUANTITY - Default quantity when selecting item
 * @property {Object} shipping - Shipping status configuration
 * @property {number} shipping.STUCK_THRESHOLD_DAYS - Days before shipment considered stuck
 * @property {Object} media - Media upload configuration
 * @property {number} media.MAX_UPLOAD_RETRIES - Maximum retry attempts for media upload
 * @property {string[]} media.SUPPORTED_TYPES - Valid media types
 * @property {Object} interface - Customer interface configuration
 * @property {string[]} interface.CUSTOM_TYPES - Interface types that require custom handling
 * @property {Object} tipsDrawer - Tips drawer configuration
 * @property {boolean} tipsDrawer.ENABLED - Whether to show tips drawer during loading
 */

/**
 * @type {CustomerReturnConfig}
 */
export const CUSTOMER_RETURN_CONFIG = {
  // Session management
  session: {
    TIMEOUT_MS: 1800000, // 30 minutes
  },

  // Item defaults
  item: {
    DEFAULT_SELECTED_QUANTITY: 1,
  },

  // Shipping status thresholds
  shipping: {
    STUCK_THRESHOLD_DAYS: 7, // Consider shipment stuck after 7 days without update
  },

  shipmentStatuses: {
    ATTEMPTED_DELIVERY: 'ATTEMPTED_DELIVERY',
    CARRIER_PICKED_UP: 'CARRIER_PICKED_UP',
    CONFIRMED: 'CONFIRMED',
    DELAYED: 'DELAYED',
    DELIVERED: 'DELIVERED',
    FAILURE: 'FAILURE',
    IN_TRANSIT: 'IN_TRANSIT',
    LABEL_PRINTED: 'LABEL_PRINTED',
    LABEL_PURCHASED: 'LABEL_PURCHASED',
    OUT_FOR_DELIVERY: 'OUT_FOR_DELIVERY',
    READY_FOR_PICKUP: 'READY_FOR_PICKUP',
  },

  // Media upload
  media: {
    MAX_UPLOAD_RETRIES: 3,
    SUPPORTED_TYPES: ['BOX', 'PRODUCT', 'DAMAGE'],
  },

  // Custom interface types that require special handling
  interface: {
    CUSTOM_TYPES: ['wrongItem', 'productStateAssessment', 'packagingAssessment', 'confirmationSummary', 'discountToKeep'],
  },

  // Tips drawer configuration
  tipsDrawer: {
    ENABLED: true, // Set to false to disable tips drawer globally
  },
}

/**
 * Helper to check if an interface type is custom
 * @param {string} type - Interface type to check
 * @returns {boolean} True if type requires custom handling
 */
export function isCustomInterfaceType(type) {
  return CUSTOMER_RETURN_CONFIG.interface.CUSTOM_TYPES.includes(type)
}
